/**
 * This will soon be deprecated as we are using Gateway for MDS data
 */
import { PayloadAction } from '@reduxjs/toolkit';
import { isEmpty } from 'lodash-es';
import moment from 'moment';
import { combineEpics, ofType, StateObservable } from 'redux-observable';
import { Observable, Observer } from 'rxjs';
import {
  filter, ignoreElements, mergeMap,
  tap } from 'rxjs/operators';

import configLib from '../../../configLib';
import { CharacterEnum } from '../../enums/character.enum';
import { StockExchangeCodeType, SymbolAndStockExchangeCode } from '../../models/fundamental/types';
import { GWMarketDataQueryBucket, GWMarketDataSummaryBucket, GWQueryCase, GWRequestType } from '../../models/gateway/types';
import { ReportingCall } from '../../models/reporting/types';
import GlobalService from '../../services/Global.service';
import MarketStateCache from '../../store-util/MarketStateCache';
import SubscribeGroupCache from '../../store-util/SubscribeGroupCache';
import { extractSymbolsNotInCacheAsString } from '../../util/DataHelpers';
import { getDateTime } from '../../util/DateTimeHelpers';
import { isCallStatusReady } from '../../util/error-handling/StatusByCallHelpers';
import { extractSymbolsFromListAsString, isMarketRegularHours } from '../../util/MarketDataHelpers';
import { calculateNetEquityComponentsFromDate } from '../../util/my-account-helpers';
import { parseOrderTicketComponentToSubscribeGroup } from '../../util/TradingHelpers';
import {
  gatewayConnected,
  individualExtendedInfoSuccess,
  loadDiscoverChartData,
  processSymbolSubscribesAction,
  processSymbolUnSubscribesAction,
  reconnect,
  reconnectEventAction,
  ReconnectPayload,
} from '../common-actions';
import { accountsCompleted } from '../crm';
import { UserPreferencesRequest } from '../file/types';
import {
  getCompanyLogoAndName,
  getNewStocks,
  getNewStocksCompleted,
  getPopularStocks,
  getPopularStocksCompleted,
  getTopGainers,
  getTopGainersCompleted,
  getTopLosers,
  getTopLosersCompleted,
} from '../fundamental';
import { FundamentalCall } from '../fundamental/types';
import { processErrorInResponse, processResponse } from '../helpers';
import { PENDING_ORDERS_REQUEST_TENNSTATUS } from '../reporting/constants';
import {
  getAccountPendingOrdersSuccess,
  getAccountSummary,
  getAccountTradeHistory,
  getAccountTradeHistorySuccess,
  getNetEquityComponents,
  getOpenPositions,
  getOpenPositionsSuccess } from '../reporting/index';
import { AccountTradeHistoryResponseData, OpenPosition } from '../reporting/types';
import {
  getCurrentAccount,
  getFavoriteStocks,
  getTopMoversInitialItems,
  hasValidCookie,
  isGatewayMDFullyEnabled,
  isUserVerifiedStatus,
} from '../selectors';
import { RootState } from '..';

import { MARKET_DATA_QUERY_YEAR_LIMIT, USER_PREFERENCES_DATA_NAME } from './constants';
import { getAllInitialSymbols } from './helpers';
import {
  getCustomWatchlistData,
  getMarketDataQuery,
  getWatchlistData,
  retrieveFavoriteStocksFromServer,
  retrieveFavoriteStocksFromServerCompleted,
  retrieveFavoriteStocksFromServerFailed,
  sendFavoriteStocksToServer,
  sendFavoriteStocksToServerCompleted,
  sendFavoriteStocksToServerFailed,
} from './index';
import {
  chartSubscription,
  instrumentSnapshotSubscription,
  marketDataSubscribeTradeSubscription,
  nbboSubscription,
  symbolStatusSubscription,
} from './subscriptionHelpers';
import {
  GetWatchlistDataPayload,
  MarketDataQueryPayload,
  ProcessSymbolSubscribesPayload,
  ProcessSymbolUnSubscribesPayload,
  SubscribeGroup,
} from './types';

const connectEpic = (action$: Observable<any>, state$: StateObservable<RootState>) => action$.pipe(
  ofType(individualExtendedInfoSuccess.type, reconnect.type),
  filter(() => !(isGatewayMDFullyEnabled())), // disable market data if gw is enabled for MD feeds
  filter(action => {
    if (state$.value.marketData.connected) {
      console.warn('[market-data/connectEpic] Already connected!');
      return false;
    }
    return true;
  }),
  filter(action => (action.type !== reconnect.type || ((action.payload as ReconnectPayload).marketData ?? false))),
  filter(action => {
    // ART-443 Temporary fix for web deployment
    if (configLib.isWeb && !configLib.__DEV__) {
      console.warn('[market-data/connectEpic] Skip connecting MD for Web');
      return false;
    }
    return true;
  }),
  tap(action => GlobalService.marketDataService.connect()),
  ignoreElements(),
);

/**
 * Loads all initial lists of symbols. Later on used by `loadSymbolDataForInitialListsEpic`
 */
const loadInitialSymbolListsEpic = (action$: Observable<any>, state$: StateObservable<RootState>) => action$.pipe(
  ofType(gatewayConnected.type, accountsCompleted.type), // TODO: Rename `accountsCompleted` to `getAccountsCompleted`
  filter(() => !state$.value.marketData.isInitialPriceDataLoaded),
  filter(() => hasValidCookie(state$.value)),
  mergeMap(action => new Observable((observer: Observer<any>) => {
    switch (action.type) {
      case gatewayConnected.type: {
        const defaultTopMoversFilter = state$.value.fundamental.topGainersAndLosersFilterValues; // TODO: Sync with FILTER_RANGE_OPTIONS in Web

        // watchlist (popular)
        observer.next(getPopularStocks());

        // discover (top gainers/losers, new on the market, popular groups (sectors), divident payers)
        observer.next(getTopGainers(defaultTopMoversFilter));
        observer.next(getTopLosers(defaultTopMoversFilter));
        observer.next(getNewStocks());
        break;
      }

      case accountsCompleted.type: {
        // my account (my stocks, pending orders, net equite chart)
        observer.next(getOpenPositions());
        observer.next(getAccountTradeHistory({
          tenNStatuses_StringList: PENDING_ORDERS_REQUEST_TENNSTATUS,
          isPendingOrdersCall: true,
          caller: 'loadInitialSymbolListsEpic',
        }));
        const accountReferenceId = getCurrentAccount(state$.value);
        const toDate = getDateTime();
        const fromDate = calculateNetEquityComponentsFromDate('1M');
        observer.next(getNetEquityComponents({ accountReferenceId, fromDate, toDate }));
        observer.next(getAccountSummary());
        break;
      }

      default:
        break;
    }
  })),
);

const loadOnlyInitialSymbolListsEpic = (action$: Observable<any>, state$: StateObservable<RootState>) => action$.pipe(
  ofType(reconnectEventAction.type),
  filter(() => hasValidCookie(state$.value)),
  mergeMap(action => new Observable((observer: Observer<any>) => {
    const defaultTopMoversFilter = state$.value.fundamental.topGainersAndLosersFilterValues;

    // watchlist (popular)
    observer.next(getPopularStocks());

    // my stocks
    observer.next(getOpenPositions());

    // discover (top gainers/losers, new on the market)
    observer.next(getTopGainers(defaultTopMoversFilter));
    observer.next(getTopLosers(defaultTopMoversFilter));
    observer.next(getNewStocks());

    const symbols = SubscribeGroupCache.getSymbolsBySelectedComponent();
    if (!symbols) {
      return;
    }

    const mappedSymbols: SymbolAndStockExchangeCode[] = symbols?.map(
      item => ({ symbol: item, stockExchangeCode: StockExchangeCodeType.US_NASDAQ }), // TODO:HW-1165  Get the stock exchange code from the API response
    );

    if (isEmpty(mappedSymbols)) {
      return;
    }

    marketDataSubscribeTradeSubscription(mappedSymbols);
    nbboSubscription(mappedSymbols);
    chartSubscription(mappedSymbols,
      [ GWMarketDataSummaryBucket.OneDay ]);
  })),
);

/**
 * Check when loaded is done in `store-util/InitialSymbolsDataLoadingStatusCache.ts` -> function `updateStatus`
 * When done `confirmInitialPriceDataLoaded` is dispatched and `marketData.isInitialPriceDataLoaded` is set
 */
const loadSymbolDataForInitialListsEpic = (
  action$: Observable<any>,
  state$: StateObservable<RootState>,
) => action$.pipe(
  ofType(
    getPopularStocksCompleted.type,
    getTopGainersCompleted.type,
    getTopLosersCompleted.type,
    getNewStocksCompleted.type,
    getOpenPositionsSuccess.type,
    getAccountTradeHistorySuccess.type,
  ),
  filter(() => !state$.value.marketData.isInitialPriceDataLoaded || state$.value.gateway.isReconnect),
  filter(() => isCallStatusReady(FundamentalCall.getPopularStocks, state$.value.fundamental.statusByCall)),
  filter(() => isCallStatusReady(FundamentalCall.getNewStocks, state$.value.fundamental.statusByCall)),
  filter(() => getTopMoversInitialItems().isInitialDataLoaded),
  filter(() => (
    !isUserVerifiedStatus(state$.value.crm.individualExtendedInfo)
    || isCallStatusReady(ReportingCall.getOpenPositions, state$.value.reporting.statusByCall)
  )),
  mergeMap(action => new Observable((observer: Observer<any>) => {
    const mappedSymbols: SymbolAndStockExchangeCode[] = getAllInitialSymbols(state$.value);
    instrumentSnapshotSubscription(mappedSymbols);
    marketDataSubscribeTradeSubscription(mappedSymbols);
    nbboSubscription(mappedSymbols);
    chartSubscription(mappedSymbols,
      [ GWMarketDataSummaryBucket.OneDay, GWMarketDataSummaryBucket.ThreeMonths ]);
  })),
);

const getWatchlistDataEpic = (
  action$: Observable<any>,
  state$: StateObservable<RootState>,
) => action$.pipe(
  ofType(getWatchlistData.type),
  filter((action: PayloadAction<GetWatchlistDataPayload>) => action.payload.symbolOrSymbols?.length > 0),
  mergeMap(action => new Observable((observer: Observer<any>) => {
    const {
      symbolOrSymbols,
      component,
    } = action.payload;
    const group: SubscribeGroup | null = parseOrderTicketComponentToSubscribeGroup(component);

    // get chart data
    const symbolArray = symbolOrSymbols.split(CharacterEnum.Comma);
    const mappedSymbols: SymbolAndStockExchangeCode[] = symbolArray.map(
      item => ({ symbol: item, stockExchangeCode: StockExchangeCodeType.US_NASDAQ }), // TODO:HW-1165  Get the stock exchange code from the API response
    );
    chartSubscription(mappedSymbols,
      [ GWMarketDataSummaryBucket.OneDay, GWMarketDataSummaryBucket.ThreeMonths ]);

    // get company logo and name
    if (component !== 'watchlist/popular' && group !== 'discover') {
      observer.next(getCompanyLogoAndName(extractSymbolsNotInCacheAsString(state$.value, symbolOrSymbols)));
    }

    // subscribe to trade feed
    if (group) {
      marketDataSubscribeTradeSubscription(mappedSymbols);
    }
  })),
);

const getCustomWatchlistDataEpic = (
  action$: Observable<any>,
  state$: StateObservable<RootState>,
) => action$.pipe(
  ofType(getCustomWatchlistData.type),
  filter((action: PayloadAction<GetWatchlistDataPayload>) => action.payload.symbolOrSymbols?.length > 0),
  mergeMap(action => new Observable((observer: Observer<any>) => {
    const fallbackSymbols = extractSymbolsFromListAsString(state$.value.reporting.peopleAlsoTradeData);
    const {
      symbolOrSymbols = fallbackSymbols,
      component,
    } = action.payload;
    const group: SubscribeGroup | null = parseOrderTicketComponentToSubscribeGroup(component);
    const symbols = symbolOrSymbols.split(',');

    // get chart data
    if (isMarketRegularHours(MarketStateCache.get().marketState)) {
      const mappedSymbols: SymbolAndStockExchangeCode[] = symbols.map(
        item => ({ symbol: item, stockExchangeCode: StockExchangeCodeType.US_NASDAQ }), // TODO:HW-1165  Get the stock exchange code from the API response
      );
      chartSubscription(mappedSymbols,
        [ GWMarketDataSummaryBucket.OneDay ]);
    }

    // get company logo and name
    observer.next(getCompanyLogoAndName(extractSymbolsNotInCacheAsString(state$.value, symbolOrSymbols)));
  })),
);

export const processSymbolSubscribesEpic = (
  action$: Observable<any>,
  state$: StateObservable<RootState>,
) => action$.pipe(
  ofType(processSymbolSubscribesAction.type),
  tap(action => {
    const actionPayload: ProcessSymbolSubscribesPayload = action.payload;
    const symbolOrSymbols = actionPayload.symbolOrSymbols;
    const symbolArr = symbolOrSymbols.split(CharacterEnum.Comma);

    const mappedSymbols: SymbolAndStockExchangeCode[] = symbolArr.map(
      item => ({ symbol: item, stockExchangeCode: StockExchangeCodeType.US_NASDAQ }), // TODO:HW-1165  Get the stock exchange code from the API response
    );

    nbboSubscription(mappedSymbols);
    marketDataSubscribeTradeSubscription(mappedSymbols);
  }),
  tap((action: PayloadAction<ProcessSymbolSubscribesPayload>) => {
    const { isFavorites, symbolOrSymbols } = action.payload;
    if (isFavorites) {
      const symbolArr = symbolOrSymbols.split(CharacterEnum.Comma);
      const mappedSymbols: SymbolAndStockExchangeCode[] = symbolArr.map(
        item => ({ symbol: item, stockExchangeCode: StockExchangeCodeType.US_NASDAQ }), // TODO:HW-1165  Get the stock exchange code from the API response
      );

      chartSubscription(mappedSymbols,
        [ GWMarketDataSummaryBucket.OneDay, GWMarketDataSummaryBucket.ThreeMonths ]);
    }
  }),
  ignoreElements(),
);

export const processSymbolUnSubscribesEpic = (
  action$: Observable<PayloadAction<ProcessSymbolUnSubscribesPayload>>,
  state$: StateObservable<RootState>,
) => action$.pipe(
  ofType(processSymbolUnSubscribesAction.type),
  tap(action => {
    const actionPayload: ProcessSymbolUnSubscribesPayload = action.payload;
    const symbolOrSymbols = actionPayload.symbolOrSymbols;
    const symbolArr = symbolOrSymbols.split(CharacterEnum.Comma);

    const mappedSymbols: SymbolAndStockExchangeCode[] = symbolArr.map(
      item => ({ symbol: item, stockExchangeCode: StockExchangeCodeType.US_NASDAQ }), // TODO:HW-1165  Get the stock exchange code from the API response
    );

    nbboSubscription(mappedSymbols, true);
    marketDataSubscribeTradeSubscription(mappedSymbols, true);
  }),
  ignoreElements(),
);

/** Subscribes positions in my account (customData.symbol is not defined - it is defined when calling from Symbol Details) */
const subscribePositionSymbolsEpic = (action$: Observable<any>, state$: StateObservable<RootState>) => action$.pipe(
  ofType(getOpenPositionsSuccess.type),
  filter(action => state$.value.marketData.isInitialPriceDataLoaded),
  filter(action => action.payload.length),
  filter(action => !action.payload.customData?.symbol),
  tap((action: PayloadAction<OpenPosition[]>) => {
    const positions = action.payload;

    const mappedSymbols: SymbolAndStockExchangeCode[] = positions?.map(
      item => ({ symbol: item.symbol, stockExchangeCode: StockExchangeCodeType.US_NASDAQ }), // TODO:HW-1165  Get the stock exchange code from the API response
    );

    if (isEmpty(mappedSymbols)) {
      return;
    }

    marketDataSubscribeTradeSubscription(mappedSymbols);
    instrumentSnapshotSubscription(mappedSymbols);
    symbolStatusSubscription(mappedSymbols);
    chartSubscription(mappedSymbols,
      [ GWMarketDataSummaryBucket.OneDay, GWMarketDataSummaryBucket.ThreeMonths ]);
  }),
  ignoreElements(),
);

const subscribePendingOrdersSymbolsEpic = (
  action$: Observable<any>,
  state$: StateObservable<RootState>,
) => action$.pipe(
  ofType(getAccountPendingOrdersSuccess.type),
  filter(action => action.payload.length),
  filter(action => !action.payload?.customData?.isSymbolDetails),
  tap((action: PayloadAction<AccountTradeHistoryResponseData[]>) => {
    const orders = action.payload;
    const mappedSymbols: SymbolAndStockExchangeCode[] = orders?.map(
      item => ({ symbol: item.symbol, stockExchangeCode: StockExchangeCodeType.US_NASDAQ }), // TODO:HW-1165  Get the stock exchange code from the API response
    );

    if (isEmpty(mappedSymbols)) {
      return;
    }

    marketDataSubscribeTradeSubscription(mappedSymbols);
    instrumentSnapshotSubscription(mappedSymbols);
    symbolStatusSubscription(mappedSymbols);
    chartSubscription(mappedSymbols,
      [ GWMarketDataSummaryBucket.OneDay, GWMarketDataSummaryBucket.ThreeMonths ]);
  }),
  ignoreElements(),
);

/**
 * Get Market Data OHLCV for 1s, 1m and 1d resolution
 */
const getMarketDataQueryEpic = (action$: Observable<any>) => action$.pipe(
  ofType(getMarketDataQuery.type),
  filter(action => {
    const { startTime } = action.payload;
    const startTimeMoment = moment(startTime).utc();
    const startDateLimit = moment().utc().subtract(MARKET_DATA_QUERY_YEAR_LIMIT, 'years');

    // Compare the start time with 15 years ago
    return !startTimeMoment.isSameOrBefore(startDateLimit);
  }),
  mergeMap((action: PayloadAction<MarketDataQueryPayload>) => new Observable((observer: Observer<any>) => {
    const { symbols, bucket, endTime, startTime } = action.payload;
    GlobalService.gatewayWs.sendRequest(GWRequestType.MarketDataQuery, {
      symbols,
      bucket: bucket || GWMarketDataQueryBucket.OneDay,
      endTime,
      startTime,
    }, GWQueryCase.OHLCV);
  })),
);

const loadDiscoverChartDataEpic = (
  action$: Observable<any>,
  state$: StateObservable<RootState>,
) => action$.pipe(
  ofType(loadDiscoverChartData.type),
  mergeMap(action => new Observable((observer: Observer<any>) => {
    const { payload } = action;
    const symbols = payload.split(CharacterEnum.Comma);
    const mappedSymbols: SymbolAndStockExchangeCode[] = symbols.map(
      item => ({ symbol: item, stockExchangeCode: StockExchangeCodeType.US_NASDAQ }), // TODO:HW-1165  Get the stock exchange code from the API response
    );
    instrumentSnapshotSubscription(mappedSymbols);
    marketDataSubscribeTradeSubscription(mappedSymbols);
    chartSubscription(mappedSymbols,
      [ GWMarketDataSummaryBucket.OneDay, GWMarketDataSummaryBucket.ThreeMonths ]);
  })),
);

export const sendFavoriteStocksToServerEpic = (
  action$: Observable<any>,
  state$: StateObservable<RootState>,
) => action$.pipe(
  ofType(sendFavoriteStocksToServer.type, processSymbolSubscribesAction.type, processSymbolUnSubscribesAction.type),
  filter(action => {
    if (action.type === processSymbolSubscribesAction.type) { // add action
      const payload = action.payload as ProcessSymbolSubscribesPayload;
      return !!payload.isFavorites;
    }

    if (action.type === processSymbolUnSubscribesAction.type) { // remove action
      const payload = action.payload as ProcessSymbolUnSubscribesPayload;
      return !!payload.isRemovingFromFavorites && !!payload.symbolOrSymbols;
    }

    return action.type === sendFavoriteStocksToServer.type;
  }),
  mergeMap(
    action => new Observable((observer: Observer<any>) => {
      const { base64 } = configLib;
      const callName = 'marketData/sendFavoriteStocksToServer';
      const favoriteStocks = base64.encode(JSON.stringify({
        favouriteStocks: getFavoriteStocks(state$.value),
      }));
      const request: UserPreferencesRequest = {
        fileData: favoriteStocks,
        preferenceName: USER_PREFERENCES_DATA_NAME,
      };

      GlobalService.fileService
        .uploadUserPreferenceFile(request)
        .then((response: any) => {
          processResponse(
            callName,
            {},
            response,
            observer,
            sendFavoriteStocksToServerCompleted,
            sendFavoriteStocksToServerFailed,
          );
        })
        .catch((error: any) => processErrorInResponse(
          callName,
          error,
          action,
          observer,
          sendFavoriteStocksToServerFailed,
        ));
    }),
  ),
);

const retrieveFavoriteStocksFromServerEpic = (
  action$: Observable<any>,
  state$: StateObservable<RootState>,
) => action$.pipe(
  ofType(retrieveFavoriteStocksFromServer.type, individualExtendedInfoSuccess.type),
  filter(() => hasValidCookie(state$.value)),
  mergeMap(
    action => new Observable((observer: Observer<any>) => {
      const callName = 'marketData/retrieveFavoriteStocksFromServer';
      const { user_correlation_id: userId } = state$.value.crm?.individualExtendedInfo ?? {};

      GlobalService.fileService
        .getUserPreferenceFile(USER_PREFERENCES_DATA_NAME)
        .then((response: any) => {
          processResponse(
            callName,
            {},
            response,
            observer,
            retrieveFavoriteStocksFromServerCompleted,
            retrieveFavoriteStocksFromServerFailed,
            false,
            { userId },
          );
        })
        .catch((error: any) => processErrorInResponse(
          callName,
          error,
          action,
          observer,
          retrieveFavoriteStocksFromServerFailed,
        ));
    }),
  ),
);


export default combineEpics(
  connectEpic,
  getCustomWatchlistDataEpic,
  getMarketDataQueryEpic,
  getWatchlistDataEpic,
  loadDiscoverChartDataEpic,
  loadInitialSymbolListsEpic,
  loadOnlyInitialSymbolListsEpic,
  loadSymbolDataForInitialListsEpic,
  processSymbolSubscribesEpic,
  subscribePendingOrdersSymbolsEpic,
  subscribePositionSymbolsEpic,
  sendFavoriteStocksToServerEpic,
  retrieveFavoriteStocksFromServerEpic,
);
